/**
 * Soft “still need a temp” for ad-hoc quick-pick templates (Dashboard).
 * Mirrors Express foodSafetyStillNeeded — keep behaviour aligned.
 */

import { FOOD_SAFETY_SCHEDULE_MODE } from 'src/constants/foodSafety'

const normalizeName = value =>
  String(value || '')
    .trim()
    .toLowerCase()

export const coveredNamesFromLogs = (logs = []) => {
  const names = new Set()
  for (const log of logs) {
    const name = normalizeName(log?.definitionName)
    if (name) names.add(name)
  }
  return names
}

export const listStillNeededAdHocTemplates = ({
  definitions = [],
  logs = [],
} = {}) => {
  const templates = (definitions || []).filter(
    d => d && d.scheduleMode === FOOD_SAFETY_SCHEDULE_MODE.AD_HOC,
  )
  if (templates.length === 0) return []

  const loggedIds = new Set(
    (logs || [])
      .map(l => (l?.definitionId != null ? String(l.definitionId) : ''))
      .filter(Boolean),
  )
  const coveredNames = coveredNamesFromLogs(logs)

  return templates.filter(def => {
    if (loggedIds.has(String(def._id))) return false
    const name = normalizeName(def.name)
    if (name && coveredNames.has(name)) return false
    return true
  })
}

export const listStillNeededFromDayItems = ({
  items = [],
  orphanLogs = [],
} = {}) => {
  const definitions = (items || []).map(i => i?.definition).filter(Boolean)
  const linkedLogs = (items || []).flatMap(i => i?.logs || [])
  return listStillNeededAdHocTemplates({
    definitions,
    logs: [...linkedLogs, ...(orphanLogs || [])],
  })
}

export const formatStillNeededTempStrip = (
  templates,
  { maxNames = 4 } = {},
) => {
  const names = (templates || [])
    .map(t => String(t?.name || '').trim())
    .filter(Boolean)
  if (names.length === 0) return ''
  if (names.length === 1) return `Still need a temp: ${names[0]}`
  const shown = names.slice(0, maxNames)
  const extra = names.length - shown.length
  const list = shown.join(', ')
  if (extra > 0) {
    return `Still need a temp: ${list} +${extra} more`
  }
  return `Still need a temp: ${list}`
}

export const formatStillNeededHomeDetail = ({
  stillNeededCount = 0,
  stillNeededNames = [],
  logCount = 0,
  viewingToday = true,
} = {}) => {
  const n = Number(stillNeededCount) || 0
  if (n <= 0) return null
  const names = (stillNeededNames || []).filter(Boolean)
  let needLine = ''
  if (n === 1 && names[0]) {
    needLine = `Still need a temp for ${names[0]}`
  } else if (n === 1) {
    needLine = '1 still needs a temp'
  } else {
    needLine = `${n} still need a temp`
  }
  if (logCount > 0) {
    const scope = viewingToday ? 'today' : 'this day'
    const logs = logCount === 1 ? `1 log ${scope}` : `${logCount} logs ${scope}`
    return `${logs} · ${needLine.replace(/^Still need/, 'still need')}`
  }
  return needLine
}

export const summarizeStillNeededAdHoc = ({
  definitions = [],
  logs = [],
  maxNames = 3,
} = {}) => {
  const stillNeeded = listStillNeededAdHocTemplates({ definitions, logs })
  return {
    stillNeededCount: stillNeeded.length,
    stillNeededNames: stillNeeded
      .map(d => String(d.name || '').trim())
      .filter(Boolean)
      .slice(0, maxNames),
    templateCount: (definitions || []).filter(
      d => d && d.scheduleMode === FOOD_SAFETY_SCHEDULE_MODE.AD_HOC,
    ).length,
  }
}
